import { Close } from '@mui/icons-material';
import { TabContext, TabList } from '@mui/lab';
import { IconButton, Stack, Tab, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { setActiveWindow, setTransportSelectedItem, setTransportSelectedItemConditionOptions, setTransportTab, setTransportWindowMinimize, } from 'app/actions/map';
import { MaximizeIcon, MinimizeIcon } from 'app/icons';
import { MapTransportPicker } from 'app/pages/Map';
import TabPanelBroadcast from 'app/pages/Map/MapTransportWindow/TabPanelBroadcast';
import TabPanelChart from 'app/pages/Map/MapTransportWindow/TabPanelChart';
import TabPanelCondition from 'app/pages/Map/MapTransportWindow/TabPanelCondition/TabPanelCondition';
import TabPanelEvent from 'app/pages/Map/MapTransportWindow/TabPanelEvent';
import TabPanelFlight from 'app/pages/Map/MapTransportWindow/TabPanelFlight';
import TabPanelInfo from 'app/pages/Map/MapTransportWindow/TabPanelInfo';
import { useAsyncAction, useInterval, usePermission, useRoles } from 'app/services';
import { transportBroadcastLoad, transportCommandSend, transportConditionLoad, transportTaskInfoLoad } from 'app/store/map';
import { hasVideoSource } from 'app/video';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { useDispatch, useSelector } from 'react-redux';
const CONDITION_RELATED_TABS = ['flight', 'chart', 'condition', 'event'];
const MapTransportWindow = () => {
    const dispatch = useDispatch();
    const guardPermission = usePermission('guard');
    const sensorPermission = usePermission('sensor');
    const fuelLevelPermission = usePermission('fuel_level');
    const isSuperAdmin = useRoles([config.userRoles['SUPERADMIN']]);
    const { activeWindow, transportWindowMinimize, transportTab } = useSelector((state) => state.map);
    const { transportSelectedItem, transportSelectedDate, transportConditionSelectedItem } = useSelector((state) => state.map);
    const { asyncAction: commandSubmit } = useAsyncAction(transportCommandSend);
    const { asyncAction: fetchConditionData, isLoading: isFetchConditionLoading } = useAsyncAction(transportConditionLoad, [
        transportSelectedItem === null || transportSelectedItem === void 0 ? void 0 : transportSelectedItem.id,
    ]);
    const { asyncAction: fetchBroadcastData, isLoading: isFetchBroadcastLoading, isError: isFetchBroadcastError, } = useAsyncAction(transportBroadcastLoad, [transportSelectedItem === null || transportSelectedItem === void 0 ? void 0 : transportSelectedItem.id]);
    const { asyncAction: fetchTaskInfoData } = useAsyncAction(transportTaskInfoLoad);
    const [fetchStatuses, setFetchStatuses] = useState([]);
    const [datetimeInterval, setDatetimeInterval] = useState(moment());
    const transportConditionListRef = useRef(null);
    const transportConditionItems = useMemo(() => {
        var _a;
        if (!transportSelectedItem) {
            return [];
        }
        return ((_a = transportSelectedItem.conditions) !== null && _a !== void 0 ? _a : []).filter((condition) => condition.type === 'message');
    }, [transportSelectedItem]);
    const handleActiveWindow = () => dispatch(setActiveWindow('transport'));
    const handleWindowCloseClick = () => dispatch(setTransportSelectedItem(null));
    const handleMinimizeClick = () => dispatch(setTransportWindowMinimize(!transportWindowMinimize));
    const handleTabChange = (_event, value) => dispatch(setTransportTab(value));
    const handleConditionOptionsOpenClick = () => {
        var _a;
        if (!transportSelectedItem) {
            return;
        }
        dispatch(setTransportSelectedItemConditionOptions(Object.assign(Object.assign({}, transportSelectedItem.conditionOptions), { open: !((_a = transportSelectedItem.conditionOptions) === null || _a === void 0 ? void 0 : _a.open) })));
    };
    const handleConditionGuardOutClick = (number) => {
        if (!transportSelectedItem) {
            return;
        }
        commandSubmit(transportSelectedItem.id, `GC ${number}`);
    };
    const handleLogKey = (e) => {
        var _a, _b;
        switch (e.code) {
            case 'ArrowUp':
                e.preventDefault();
                (_a = transportConditionListRef.current) === null || _a === void 0 ? void 0 : _a.handleKeyUp();
                break;
            case 'ArrowDown':
                e.preventDefault();
                (_b = transportConditionListRef.current) === null || _b === void 0 ? void 0 : _b.handleKeyDown();
                break;
        }
    };
    const declOfNum = (number, titles) => {
        return titles[number % 100 > 4 && number % 100 < 20 ? 2 : [2, 0, 1, 1, 1, 2][number % 10 < 5 ? number % 10 : 5]];
    };
    const handleBroadcastRefresh = () => {
        if (!transportSelectedItem) {
            return;
        }
        if (hasVideoSource(transportSelectedItem)) {
            fetchBroadcastData(transportSelectedItem.id);
        }
    };
    useInterval(() => {
        if (moment().seconds() === 0) {
            setDatetimeInterval(moment());
        }
    }, 1000);
    useEffect(() => {
        setFetchStatuses([]);
    }, [transportSelectedItem === null || transportSelectedItem === void 0 ? void 0 : transportSelectedItem.id, transportSelectedDate]);
    useEffect(() => {
        if (!transportSelectedItem) {
            return;
        }
        if (fetchStatuses.includes(transportTab)) {
            return;
        }
        switch (transportTab) {
            case 'info':
                fetchTaskInfoData(transportSelectedItem.id);
                break;
            case 'broadcast':
                if (hasVideoSource(transportSelectedItem)) {
                    fetchBroadcastData(transportSelectedItem.id);
                }
                break;
            case 'flight':
            case 'chart':
            case 'condition':
            case 'event':
                if (!fetchStatuses.some((status) => CONDITION_RELATED_TABS.includes(status))) {
                    fetchConditionData(transportSelectedItem.id);
                }
                break;
        }
        setFetchStatuses([...fetchStatuses, transportTab]);
    }, [transportTab, fetchStatuses, transportSelectedItem]);
    useEffect(() => {
        document.addEventListener('keydown', handleLogKey);
        return () => {
            document.removeEventListener('keydown', handleLogKey);
        };
    }, []);
    if (!transportSelectedItem) {
        return React.createElement(React.Fragment, null);
    }
    const hasGroundSensor = Boolean(transportSelectedItem.ground_sensor);
    const transportWindowHeight = transportTab === 'chart' ? 923 : hasGroundSensor ? 711 : 678;
    return (React.createElement(Draggable, { bounds: 'parent', handle: '.header-draggable', disabled: transportWindowMinimize },
        React.createElement(Stack, { direction: 'column', onClick: handleActiveWindow, sx: (theme) => {
                var _a;
                return (Object.assign(Object.assign({ position: 'absolute', right: '80px', top: theme.spacing(4), minWidth: '804px', maxWidth: '804px', height: `${transportWindowHeight}px`, maxHeight: transportWindowMinimize ? `${transportWindowHeight}px` : '100%', zIndex: '1050', overflow: 'hidden', bgcolor: 'background.paper', boxShadow: '0 1px 3px ' + alpha((_a = theme.palette.grey.main) !== null && _a !== void 0 ? _a : theme.palette.grey[500], 0.12), borderRadius: theme.shape.borderRadius, transform: 'translate(0px, 0px)', '.header-draggable': {
                        width: '100%',
                        cursor: 'grab',
                        '.MuiOutlinedInput-input': {
                            padding: theme.spacing(0),
                        },
                        '.MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '.MuiInputAdornment-positionEnd': {
                            margin: theme.spacing(0),
                        },
                    }, '&.react-draggable-dragging .header-draggable': {
                        cursor: 'grabbing',
                    } }, (transportWindowMinimize && {
                    top: 'auto',
                    right: '0',
                    bottom: '0',
                    width: 'auto',
                    minWidth: '300px',
                    height: '100%',
                    transform: `translateY(${transportWindowHeight - 44}px) !important`,
                    borderTopRightRadius: '0',
                    borderBottomLeftRadius: '0',
                    borderBottomRightRadius: '0',
                    '.header-draggable': {
                        cursor: 'default',
                    },
                })), (activeWindow === 'transport' && {
                    zIndex: '1051',
                })));
            } },
            React.createElement(TabContext, { value: transportTab },
                React.createElement(Stack, { direction: 'row', sx: (theme) => ({
                        userSelect: 'none',
                        bgcolor: 'background.default',
                        '.MuiAvatar-root': {
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                        },
                        '.MuiTypography-root': {
                            fontSize: '13px',
                        },
                        '.header-draggable': Object.assign({ width: '100%', minHeight: '44px', padding: '10px 10px 10px 10px', '.MuiTypography-root': {
                                padding: theme.spacing(0, 2),
                            } }, (transportWindowMinimize && {
                            paddingLeft: '0',
                        })),
                    }) },
                    React.createElement(Stack, { direction: 'row', alignItems: 'center', component: 'ul', sx: (theme) => (Object.assign({ left: '0', top: '9px', marginRight: 'auto', padding: '0', listStyle: 'none', margin: theme.spacing(0, 2), li: {
                                margin: '0',
                                padding: '0',
                            } }, (transportWindowMinimize && {
                            display: 'none',
                        }))) },
                        React.createElement("li", null,
                            React.createElement(MapTransportPicker, null))),
                    React.createElement(Stack, { direction: 'row', justifyContent: 'start', alignItems: 'center', className: 'header-draggable' },
                        React.createElement(Typography, { variant: 'h6', component: 'span' }, transportSelectedItem.name)),
                    React.createElement(Stack, { direction: 'row', alignItems: 'center', component: 'ul', sx: (theme) => ({
                            position: 'absolute',
                            right: '8px',
                            top: '7px',
                            margin: '0',
                            marginLeft: 'auto',
                            listStyle: 'none',
                            paddingLeft: 0,
                            li: {
                                margin: '0',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                button: {
                                    width: '30px',
                                    height: '30px',
                                    '.maximizeIcon, .minimizeIcon': {
                                        width: '16px',
                                        height: '16px',
                                    },
                                },
                            },
                            'li:not(:last-of-type) path': {
                                fill: theme.palette.text.secondary,
                            },
                        }) },
                        React.createElement("li", null,
                            React.createElement(IconButton, { color: 'default', onClick: handleMinimizeClick },
                                transportWindowMinimize && React.createElement(MaximizeIcon, { className: 'maximizeIcon', fontSize: 'small' }),
                                !transportWindowMinimize && React.createElement(MinimizeIcon, { className: 'minimizeIcon', fontSize: 'small' }))),
                        React.createElement("li", null,
                            React.createElement(IconButton, { color: 'default', onClick: handleWindowCloseClick },
                                React.createElement(Close, { fontSize: 'small' }))))),
                React.createElement(Stack, { direction: 'row', alignItems: 'center', sx: Object.assign(Object.assign(Object.assign({}, (isFetchConditionLoading && {
                        borderColor: 'transparent',
                    })), (transportTab === 'info' && {
                        borderBottom: '0',
                        '.MuiInput-root': {
                            opacity: '0',
                            visibility: 'hidden',
                        },
                    })), (transportWindowMinimize && {
                        width: '0px !important',
                        opacity: '0',
                        visibility: 'hidden',
                    })) },
                    React.createElement(TabList, { onChange: handleTabChange, TabIndicatorProps: { children: React.createElement("span", null) }, sx: (theme) => ({
                            width: '100%',
                            minHeight: '44px',
                            '.MuiTab-root': {
                                fontSize: '12px',
                                minHeight: '44px',
                                padding: theme.spacing(2),
                                '&.Mui-selected': {
                                    color: 'inherit',
                                },
                            },
                            '& .MuiTabs-indicator': {
                                display: 'flex',
                                justifyContent: 'center',
                                backgroundColor: 'transparent',
                            },
                            '& .MuiTabs-indicator span': {
                                width: 'calc(100% - ' + theme.spacing(4) + ')',
                                bgcolor: theme.palette.primary.main,
                                borderRadius: theme.shape.borderRadius,
                            },
                        }) },
                        React.createElement(Tab, { value: 'info', label: '\u0418\u043D\u0444\u043E\u0440\u043C\u0430\u0446\u0438\u044F', disableRipple: true }),
                        React.createElement(Tab, { value: 'broadcast', label: '\u0412\u0438\u0434\u0435\u043E', disableRipple: true }),
                        React.createElement(Tab, { value: 'flight', label: '\u0420\u0435\u0439\u0441\u044B', disableRipple: true }),
                        React.createElement(Tab, { value: 'chart', label: '\u0413\u0440\u0430\u0444\u0438\u043A\u0438', disableRipple: true }),
                        React.createElement(Tab, { value: 'condition', label: '\u0421\u043E\u0441\u0442\u043E\u044F\u043D\u0438\u044F', disableRipple: true }),
                        React.createElement(Tab, { value: 'event', label: '\u0416\u0443\u0440\u043D\u0430\u043B \u0441\u043E\u0431\u044B\u0442\u0438\u0439', disableRipple: true }))),
                React.createElement(Stack, { direction: 'row', sx: (theme) => (Object.assign(Object.assign({ display: 'none', borderBottom: '1px solid', borderColor: theme.palette.divider, padding: '4px 12px', '.MuiButtonBase-root': {
                            bgcolor: 'background.default',
                            '&.Mui-disabled': {
                                bgcolor: 'background.default',
                            },
                        } }, (transportWindowMinimize && {
                        width: '0px !important',
                        opacity: '0',
                        visibility: 'hidden',
                    })), (transportTab === 'info' && {
                        display: 'none',
                    }))) }),
                React.createElement(Stack, { direction: 'row', sx: (theme) => (Object.assign({ flex: '1 1 auto', display: 'flex', overflow: 'hidden', '.MuiTabPanel-root': {
                            width: '100%',
                            height: '100%',
                            padding: theme.spacing(0),
                        } }, (transportWindowMinimize && {
                        width: '0px',
                        opacity: '0',
                        visibility: 'hidden',
                    }))) },
                    React.createElement(React.Fragment, null,
                        React.createElement(TabPanelInfo, { transportSelectedItem: transportSelectedItem }),
                        React.createElement(TabPanelFlight, { transportSelectedItem: transportSelectedItem, isFetchConditionLoading: isFetchConditionLoading }),
                        React.createElement(TabPanelBroadcast, { transportSelectedItem: transportSelectedItem, isFetchBroadcastLoading: isFetchBroadcastLoading, isFetchBroadcastError: isFetchBroadcastError, onBroadcastRefresh: handleBroadcastRefresh }),
                        React.createElement(TabPanelChart, { isFetchConditionLoading: isFetchConditionLoading, transportConditionItems: transportConditionItems, transportSelectedItem: transportSelectedItem }),
                        React.createElement(TabPanelCondition, { transportConditionItems: transportConditionItems, isFetchConditionLoading: isFetchConditionLoading, handleConditionOptionsOpenClick: handleConditionOptionsOpenClick, transportSelectedItem: transportSelectedItem, transportSelectedDate: transportSelectedDate, guardPermission: guardPermission, fuelLevelPermission: fuelLevelPermission, relayPermission: isSuperAdmin, handleConditionGuardOutClick: handleConditionGuardOutClick, transportConditionSelectedItem: transportConditionSelectedItem, declOfNum: declOfNum, datetimeInterval: datetimeInterval, sensorPermission: sensorPermission, transportConditionListRef: transportConditionListRef }),
                        React.createElement(TabPanelEvent, { isFetchConditionLoading: isFetchConditionLoading, transportSelectedItem: transportSelectedItem })))))));
};
export default MapTransportWindow;
